import * as PIXI from "pixi.js";
import { Dictionary } from "../../util/collection/Dictionary";
import { EventObject } from "../../util/event/EventObject";
import { Logger } from "../../util/log/Logger";
import { ObjectUtil } from "../../util/object/ObjectUtil";
import { GraphicsUtil } from "../../util/view/GraphicsUtil";
import { MouseUtil } from "../../util/view/MouseUtil";
import { MouseBlocker } from "../control/MouseBlocker";
import { FactoryManager } from "../factory/FactoryManager";
import { ResizeEvent } from "../renderer/Renderer";
import { AbstractView } from "../view/AbstractView";
import { MessageDialog } from "../view/display/MessageDialog";
/**
 * DialogEvent.ts
 */
export class DialogEvent {
}
DialogEvent.ACTIVE = new EventObject("DialogEvent.ACTIVE");
DialogEvent.INACTIVE = new EventObject("DialogEvent.INACTIVE");
/**
 * DialogPending.ts
 */
export class DialogPending {
    constructor(properties, portraitProperties, data) {
        this.properties = properties;
        this.portraitProperties = portraitProperties;
        this.data = data;
    }
    updateData(data) {
        this.data = data;
    }
    isClass(name) {
        return this.properties.getClassName() == name;
    }
    getProperties() {
        return this.properties;
    }
    getPortraitProperties() {
        return this.portraitProperties;
    }
    getData() {
        return this.data;
    }
    getClass() {
        return this.properties.getClass();
    }
    getClassName() {
        return this.properties.getClassName();
    }
}
/**
 * DialogManager.ts
 */
export class DialogManager extends AbstractView {
    constructor(properties, portraitProperties) {
        super(properties, portraitProperties);
        this.run();
    }
    create() {
        this.createFactory();
        this.createModel();
        this.createCache();
        this.createPending();
        this.createBlocker();
        Logger.addObjectRef(this, "DialogManager");
    }
    createProperties(landscapeProperties, portraitProperties) {
        super.createProperties(landscapeProperties, portraitProperties);
        this.properties = landscapeProperties;
    }
    createFactory() {
        this.factory = FactoryManager.getInstance().getFactory();
    }
    createModel() {
        this.model = this.factory.getModel();
    }
    createBlocker() {
        this.blocker = new MouseBlocker(this.properties.mouseBlockerProperties);
        this.blocker.onClick(this.handleBlockerSelected, this);
        MouseUtil.addMouseBlock(this.blocker);
    }
    createCache() {
        this.cache = new Dictionary();
    }
    createPending() {
        this.pending = [];
    }
    clearMask() {
        if (this.mask) {
            this.removeChild(this.maskShape);
            this.maskShape.destroy();
            this.mask = null;
        }
    }
    createMask() {
        const viewport = this.renderer.getViewport();
        const size = new PIXI.Point(viewport.width, viewport.height);
        this.maskShape = GraphicsUtil.createMask(size);
        this.addChild(this.maskShape);
        this.mask = this.maskShape;
    }
    updateMask() {
        this.clearMask();
        this.createMask();
    }
    close() {
        this.nextDialog();
    }
    handleBlockerSelected() {
        if (this.currentDialog.isDismissable()) {
            this.currentDialog.handleCancel();
        }
    }
    addBlocker() {
        this.dispatchCore(DialogEvent.ACTIVE);
        this.addChild(this.blocker);
    }
    removeBlocker() {
        this.dispatchCore(DialogEvent.INACTIVE);
        this.removeChild(this.blocker);
    }
    preload(properties, portraitProperties, data) {
        let dialog = this.createDialog(properties, portraitProperties);
        if (data) {
            dialog.update(data);
        }
    }
    open(properties, portraitProperties, data, refresh = false) {
        if (refresh) {
            this.refreshDialog(properties, portraitProperties, data);
        }
        else {
            this.openDialog(properties, portraitProperties, data);
        }
        if (!this.currentDialog) {
            this.addBlocker();
            this.nextDialog();
        }
        this.handleRotate();
        this.dispatchCore(ResizeEvent.TRACK_TARGETS);
    }
    refreshDialog(properties, portraitProperties, data) {
        let className = properties.getClassName();
        if (this.currentDialog && this.currentDialog.isClass(className)) {
            this.currentDialog.update(data);
        }
        else {
            let pending;
            for (let i = 0; i < this.pending.length; i++) {
                pending = this.pending[i];
                if (pending.isClass(className)) {
                    pending.updateData(data);
                    return;
                }
            }
            this.pending.push(new DialogPending(properties, portraitProperties, data));
        }
    }
    openDialog(properties, portraitProperties, data) {
        this.pending.push(new DialogPending(properties, portraitProperties, data));
    }
    nextDialog() {
        this.removeDialog();
        if (this.pending.length > 0) {
            let dialog = this.getDialog(this.pending.shift());
            this.addDialog(dialog);
        }
        else {
            this.removeBlocker();
        }
    }
    addDialog(dialog) {
        this.currentDialog = dialog;
        this.addChild(this.currentDialog);
        this.currentDialog.handleAddToScene();
        this.currentDialog.eventMode = "auto";
        this.renderer.forceTracking();
    }
    removeDialog() {
        if (this.currentDialog) {
            this.currentDialog.eventMode = "none";
            this.currentDialog.unhighlightButtons();
            this.removeChild(this.currentDialog);
            this.currentDialog.handleRemoveFromScene();
            this.currentDialog = null;
        }
    }
    handleDialogComplete() {
        this.close();
    }
    getDialog(pending) {
        let className = pending.getClassName();
        let dialog;
        let data = pending.getData();
        if (this.cache.containsKey(className)) {
            dialog = this.cache.getValue(className);
        }
        else {
            dialog = this.createDialog(pending.getProperties(), pending.getPortraitProperties());
        }
        this.autoPosition(dialog);
        dialog.update(data);
        return dialog;
    }
    createDialog(properties, portraitProperties) {
        let dialog = ObjectUtil.create(properties.getClass(), properties, portraitProperties);
        dialog.onComplete(this.handleDialogComplete, this);
        this.cache.setValue(properties.getClassName(), dialog);
        return dialog;
    }
    isDialogOpen() {
        return this.currentDialog != null;
    }
    isMessageDialogOpen() {
        return this.currentDialog instanceof MessageDialog;
    }
    getProperties() {
        return this.properties;
    }
    getPortraitProperties() {
        return this.portraitProperties;
    }
    createSignposting() {
        this.signposting = this.factory.createSignPosting();
    }
    addSignposting() {
        this.createSignposting();
        this.parent.addChild(this.signposting);
    }
    removeSignposting() {
        this.parent.removeChild(this.signposting);
        this.signposting.destroy();
    }
    handleRotate(event) {
        super.handleRotate(event);
        if (this.currentDialog) {
            this.autoPosition(this.currentDialog);
        }
    }
    autoPosition(dialog) {
        dialog.autoPosition();
    }
}
//# sourceMappingURL=DialogManager.js.map